import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export const useBlockUser = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const blockUser = async (
    blockedUserId: string, 
    type: 'permanent' | 'temporary', 
    hours?: number, 
    userReason?: string
  ) => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to block users",
        variant: "destructive"
      });
      return false;
    }

    setLoading(true);
    try {
      // Create technical reason
      let technicalReason = type === 'temporary' && hours 
        ? `temporary_${hours}h_${new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()}`
        : 'permanent';
      
      // Append user reason if provided
      const finalReason = userReason?.trim() 
        ? `${technicalReason}|user_reason:${userReason.trim()}`
        : technicalReason;

      const { error } = await supabase
        .from('user_blocks')
        .upsert({
          blocker_id: user.id,
          blocked_id: blockedUserId,
          reason: finalReason
        }, {
          onConflict: 'blocker_id,blocked_id'
        });

      if (error) throw error;

      const actionText = type === 'permanent' 
        ? 'blocked permanently' 
        : `hidden for ${hours} hour${hours === 1 ? '' : 's'}`;

      toast({
        title: "User blocked",
        description: `User has been ${actionText}`
      });

      return true;
    } catch (error: any) {
      console.error('Error blocking user:', error);
      toast({
        title: "Error",
        description: "Failed to block user. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unblockUser = async (blockedUserId: string) => {
    if (!user) return false;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('user_blocks')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_id', blockedUserId);

      if (error) throw error;

      toast({
        title: "User unblocked",
        description: "User has been removed from your block list"
      });

      return true;
    } catch (error: any) {
      console.error('Error unblocking user:', error);
      toast({
        title: "Error",
        description: "Failed to unblock user",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const checkIfBlocked = async (targetUserId: string) => {
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .from('user_blocks')
        .select('id, reason')
        .eq('blocker_id', user.id)
        .eq('blocked_id', targetUserId)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) return false;

      // Check if it's a temporary block that has expired
      if (data.reason?.startsWith('temporary_')) {
        const technicalPart = data.reason.split('|')[0]; // Get technical part before user reason
        const parts = technicalPart.split('_');
        if (parts.length >= 3) {
          const expiryDate = new Date(parts.slice(2).join('_'));
          if (expiryDate < new Date()) {
            // Expired temporary block, remove it
            await unblockUser(targetUserId);
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      console.error('Error checking block status:', error);
      return false;
    }
  };

  return {
    blockUser,
    unblockUser,
    checkIfBlocked,
    loading
  };
};
